import { AddToReportButton } from '@components/common/AddToReportButton';
import ErrorBoundary from '@components/ErrorBoundary';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { DownloadSVG, ExpandSVG } from '../icon/svg';

export const ChartContainer = ({
  children,
  title,
  controller,
  height,
  icon,
  handleSaveImage,
  fullHeight,
  hideToolbar,
  chartRef,
  hideAddToReportButton,
  controller1,
  canAddToReport = true,
  canDownload = true,
  hideExpandButton,
  checkFullView,
  minHeight,
  onCustomizeClick,
  showCustomizeButton = false,
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState('');

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (chartRef?.current) {
        const chartInstance = chartRef.current.getEchartsInstance();
        // Get the base64 image URL (PNG format by default)
        const ok = chartInstance.getDataURL({
          type: 'jpeg', // You can also use 'jpeg' or 'svg'
          pixelRatio: 1.2,
          backgroundColor: '#fff', // Background color (optional)
        });
        setDesc(`<img src=${ok} alt="Chart" style="width: 595px; height: auto;" />`);
      }
    }, 1000); // Adjust the delay as needed

    return () => {
      clearTimeout(timer);
      setDesc('');
    };
  }, [chartRef, children]);

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          position: 'relative',
          width: '100%',
          // Use override if provided, else keep existing defaults (compact mode uses smaller heights)
          minHeight:
            minHeight ??
            (compact
              ? { xs: '280px', md: '280px', xl: '320px' }
              : { xs: '400px', md: '400px', xl: '600px' }),
          height: '100%',
          borderRadius: compact ? 2 : 1,
          boxShadow: compact ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
        }}
      >
        <CardHeader
          sx={{ p: 0, pb: 1 }}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box display={'flex'} alignItems={'center'} gap={1}>
                {icon}
                {title} {checkFullView && '(Check full view for better visualization)'}
                {controller1 && controller1}
              </Box>
              {hideToolbar ? null : (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}
                >
                  {showCustomizeButton && onCustomizeClick && (
                    <Tooltip title="Customize chart" placement="top" arrow>
                      <IconButton onClick={onCustomizeClick}>
                        <TuneIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDownload && (
                    <Tooltip title="Export as PNG" placement="top" arrow>
                      <IconButton onClick={handleSaveImage}>
                        <DownloadSVG />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canAddToReport && (
                    <ErrorBoundary FallbackComponent={() => null}>
                      <Tooltip
                        title={
                          !canAddToReport ? 'You can add this chart to any custom report.' : ''
                        }
                        arrow
                        placement="top"
                      >
                        {!hideAddToReportButton && (
                          <Box>
                            <AddToReportButton
                              name={title}
                              description={desc}
                              canEdit={canAddToReport}
                            />
                          </Box>
                        )}
                      </Tooltip>
                    </ErrorBoundary>
                  )}
                  {hideExpandButton ? null : (
                    <IconButton onClick={handleClickOpen}>
                      <ExpandSVG />
                    </IconButton>
                  )}
                  {controller && controller}
                </Box>
              )}
            </Box>
          }
        ></CardHeader>
        <CardContent
          sx={{
            // Allow height prop to apply across breakpoints; preserve defaults (compact mode uses smaller heights)
            height: {
              xs: fullHeight
                ? '100vh'
                : height
                  ? typeof height === 'number'
                    ? `${height}px`
                    : height
                  : compact
                    ? '220px'
                    : '400px',
              md: fullHeight
                ? '100vh'
                : height
                  ? typeof height === 'number'
                    ? `${height}px`
                    : height
                  : compact
                    ? '220px'
                    : '320px',
              xl: fullHeight
                ? '100vh'
                : height
                  ? typeof height === 'number'
                    ? `${height}px`
                    : height
                  : compact
                    ? '260px'
                    : '520px',
            },
            p: 0,
            display: 'flex',
            '& > *': {
              width: '100%',
              height: '100%',
            },
            overflow: 'auto',
          }}
        >
          {children}
        </CardContent>
      </Card>
      <Dialog open={open} fullScreen maxWidth="xl" onClose={handleClose}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box display={'flex'} alignItems={'center'} gap={1}>
              {icon}
              {title}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              {/* {showCustomizeButton && onCustomizeClick && (
                <Tooltip title="Customize chart" arrow placement="top">
                  <IconButton onClick={onCustomizeClick}>
                    <TuneIcon />
                  </IconButton>
                </Tooltip>
              )} */}
              {canAddToReport && (
                <Tooltip title="Export as PNG" arrow placement="top">
                  <IconButton onClick={handleSaveImage}>
                    <FileDownloadOutlinedIcon />
                  </IconButton>
                </Tooltip>
              )}
              {/* <Tooltip
                title="You can add this chart to any custom report. "
                arrow
              >
                <IconButton>
                  <FileAddSVG />
                </IconButton>
              </Tooltip> */}
              <IconButton onClick={handleClose}>
                <FullscreenExitIcon />
              </IconButton>
              {controller && controller}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 5 }}>
          <Box height={fullHeight ? fullHeight : '89vh'}>{children}</Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
